import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  RefreshCw,
  Volume2,
  VolumeX,
  ChefHat,
  Package,
  AlertCircle,
  CheckCircle,
  Radio,
  Home,
  ShoppingBag,
  Truck,
  Flame,
  Filter,
  LayoutGrid,
  ListChecks,
  ClipboardList,
  History,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  CalendarClock,
} from 'lucide-react'
import { cn, formatTimeHuman } from '@/lib/utils'
import apiClient from '@/api/client'
import type { User as UserType, Order, OrderItem, KitchenStation } from '@/types'
import { KOTCard, displayTicketNo } from './KOTCard'
import { RecipeCardSheet } from '@/components/recipe-card/RecipeCardSheet'
import { useRecipeCardLookup } from '@/hooks/useRecipeCards'
import type { RecipeCardTarget } from '@/lib/recipeCard/recipeCardTypes'
import { CateringBoard } from './CateringBoard'
import { CATERING_KDS_QK, useCateringOnKds } from '@/hooks/useCateringOnKds'
import { CK } from '@/lib/cateringKds/cateringKdsStrings'
import { splitTickets } from '@/lib/cateringKds/cateringKdsLogic'
import type { CateringTicket } from '@/lib/cateringKds/cateringKdsTypes'
import { AllDayPanel } from './AllDayCounts'
import { publishOrderReady } from '@/lib/kdsRealtime'
import { useToast } from '@/hooks/use-toast'
import { useKitchenSettings } from '@/hooks/useKitchenSettings'
import { useKitchenStream } from '@/lib/kitchenStream'
import { kitchenSoundService } from '@/services/soundService'

interface NewEnhancedKitchenLayoutProps {
  user: UserType
}

type LiveFilter = 'all' | 'dine_in' | 'takeout' | 'delivery' | 'urgent'

// Shared query key PREFIXES so SSE invalidations hit every variant (e.g.
// different station filters) with a single invalidate call.
const ACTIVE_ORDERS_KEY_PREFIX = ['newEnhancedKitchenOrders'] as const
const TAKEAWAY_READY_KEY = ['kitchenTakeawayReady'] as const
const KITCHEN_STATS_KEY = ['kitchen', 'stats'] as const
const COMPLETED_ORDERS_KEY_PREFIX = ['kitchenCompletedOrders'] as const

/** Format prep seconds as M:SS for the stats strip (e.g. 252 → "4:12"). */
function formatPrepTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function NewEnhancedKitchenLayout({ user }: NewEnhancedKitchenLayoutProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const kitchen = useKitchenSettings()

  const [liveFilter, setLiveFilter] = useState<LiveFilter>('all')
  const [stationFilter, setStationFilter] = useState<string>('all')
  const [showSoundSettings, setShowSoundSettings] = useState(false)
  // Seed from the shared sound service so the one on-screen control governs
  // all KDS audio (new-order alerts + bump/ready chimes) and persists.
  const [soundEnabled, setSoundEnabled] = useState(() => kitchenSoundService.getSettings().enabled)
  const [volume, setVolume] = useState(() => kitchenSoundService.getSettings().volume)
  // All-Day panel: batch-prep aggregation ("6× Wings working"), opened from the
  // header on demand. Ephemeral (not persisted) — it's a glance, not a mode.
  const [allDayOpen, setAllDayOpen] = useState(false)
  // Completed lane: today's finished tickets (view-only history). Collapsed by
  // default so the live board owns the screen; persisted so an expo screen that
  // wants it open stays open across reloads. Lazy-loaded only while open.
  const [completedOpen, setCompletedOpen] = useState(() => {
    try {
      return localStorage.getItem('kds-completed-open') === 'open'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem('kds-completed-open', completedOpen ? 'open' : 'closed')
    } catch {
      /* ignore */
    }
  }, [completedOpen])
  // Ticket order: 'newest' puts the freshest punch top-left (the user's mental
  // model — a new order lands where you're looking); 'oldest' is classic FIFO.
  // Either way, aging tickets stay obvious via the color urgency tiers + the
  // Urgent filter, so newest-first doesn't hide a forgotten ticket.
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>(() => {
    try {
      return localStorage.getItem('kds-sort-order') === 'oldest' ? 'oldest' : 'newest'
    } catch {
      return 'newest'
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem('kds-sort-order', sortOrder)
    } catch {
      /* ignore */
    }
  }, [sortOrder])
  // View mode: 'grid' (one card per ticket) or 'expo' (cards group lines by
  // station with per-station progress — for an expeditor coordinating stations).
  const [viewMode, setViewMode] = useState<'grid' | 'expo'>(() => {
    try {
      return localStorage.getItem('kds-view-mode') === 'expo' ? 'expo' : 'grid'
    } catch {
      return 'grid'
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem('kds-view-mode', viewMode)
    } catch {
      /* ignore */
    }
  }, [viewMode])
  // Keyboard / bump-bar focus: the currently-selected ticket (number keys jump,
  // arrows move, Enter bumps / picks up, R recalls). null = nothing focused.
  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null)
  // Recipe cards (kitchen flow): which line's recipe is open over the board.
  // `recipes.enabled` is false unless the deploy runs the kitchen flow AND the
  // tenant switched recipes on — then no line shows the button and nothing
  // below is ever set.
  const recipes = useRecipeCardLookup()
  const [recipeTarget, setRecipeTarget] = useState<RecipeCardTarget | null>(null)
  const recipeOpen = recipeTarget != null
  const openRecipe = useCallback(
    (item: OrderItem) => setRecipeTarget({ kind: 'order-item', orderItemId: item.id, name: item.product?.name }),
    [],
  )
  // Catering on the Kitchen Display (kitchen flow). `catering.enabled` is
  // false unless the deploy runs the kitchen flow AND the admin switched it
  // on — then there is no Catering chip, no request, and the board below is
  // exactly what it was. The chip swaps the board for the catering lane; the
  // order filters are left as they were.
  const catering = useCateringOnKds()
  const [cateringView, setCateringView] = useState(false)
  const showingCatering = cateringView && catering.enabled
  // Small-screen (< lg) board tab: Current (preparing) vs Ready (at pass).
  // Defaults to Current and never auto-switches — a cook bumping a ticket
  // stays in flow; the Ready tab announces new arrivals via its badge instead.
  const [smallTab, setSmallTab] = useState<'current' | 'ready'>('current')
  // Nonce for the floating "+1" on the Ready tab (same mechanic as the counter
  // product-card pop: a new key re-mounts the span so the animation re-fires).
  const [readyPopNonce, setReadyPopNonce] = useState(0)

  // Push the on-screen sound control into the shared service (persists to
  // localStorage and gates every kitchenSoundService.play* call below).
  useEffect(() => {
    kitchenSoundService.updateSettings({ enabled: soundEnabled, volume })
  }, [soundEnabled, volume])

  // ── Queries ──

  const {
    data: ordersResponse,
    isLoading,
    refetch,
    error,
    isFetching,
  } = useQuery({
    // stationFilter in the key means RQ transparently maintains one cache
    // per filter, so switching filters is instant once visited once.
    queryKey: [...ACTIVE_ORDERS_KEY_PREFIX, { stationFilter }] as const,
    queryFn: () => apiClient.getKitchenOrders(
      stationFilter !== 'all' ? { status: 'all', station_id: stationFilter } : 'all',
    ),
    // SSE drives the updates; polling is a fallback only (30s) in case the
    // stream drops and the browser doesn't reconnect immediately.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: 2,
    select: (data) => data.data || [],
  })

  const { data: takeawayReady = [] } = useQuery({
    queryKey: TAKEAWAY_READY_KEY,
    queryFn: async () => {
      const res = await apiClient.getOrders({ status: 'ready', order_type: 'takeout', per_page: 50 })
      return res.data ?? []
    },
    refetchInterval: 30_000,
  })

  // Completed lane — today's finished tickets (view-only history). Only fetched
  // while the lane is open (lazy), keyed by station so it respects the active
  // station filter. Kept fresh on a slow timer + SSE invalidation (a bump moves
  // a ticket into this lane).
  const { data: completedOrders = [], isLoading: completedLoading } = useQuery({
    queryKey: [...COMPLETED_ORDERS_KEY_PREFIX, { stationFilter }] as const,
    queryFn: () =>
      apiClient.getKitchenCompletedOrders(
        stationFilter !== 'all' ? { station_id: stationFilter, limit: 100 } : { limit: 100 },
      ),
    enabled: completedOpen,
    refetchInterval: completedOpen ? 30_000 : false,
    select: (data) => data.data || [],
  })

  const { data: stationsRes } = useQuery({
    queryKey: ['kitchen', 'stations'],
    queryFn: () => apiClient.getKitchenStations(),
    staleTime: 60_000,
  })

  const {
    data: cateringTickets = [],
    isLoading: cateringLoading,
    isError: cateringError,
    refetch: refetchCatering,
  } = useQuery({
    queryKey: [...CATERING_KDS_QK, { stationFilter }] as const,
    // The cache holds the ticket list itself (no `select`): the catering
    // board writes optimistic ticks and the server's fresh ticket into it.
    queryFn: () => apiClient.getKitchenCateringTickets(stationFilter).then((r) => (r.data ?? []) as CateringTicket[]),
    enabled: catering.enabled,
    // The stream drives updates; this is the fallback, and it also rolls the
    // lane over at midnight (tomorrow's plans appear).
    refetchInterval: catering.enabled ? 60_000 : false,
    retry: 1,
  })
  const cateringActiveCount = useMemo(() => splitTickets(cateringTickets).active.length, [cateringTickets])

  // Rolling-24h performance metrics for the header stats strip. Refetched on a
  // slow timer and invalidated on any kitchen event (so a bump updates it).
  const { data: kitchenStats } = useQuery({
    queryKey: KITCHEN_STATS_KEY,
    queryFn: () => apiClient.getKitchenStats(),
    refetchInterval: 60_000,
    select: (r) => r.data,
  })
  // Only KDS-output stations make sense as filters here — printer-only
  // stations never produce KDS items, so a chip for them would always
  // return an empty list and confuse the cooks.
  const stations = ((stationsRes?.data ?? []) as KitchenStation[])
    .filter((s) => s.is_active && s.output_type !== 'printer')
    .sort((a, b) => a.sort_order - b.sort_order)

  // ── SSE: event-driven cache invalidation ──

  const handleEvent = useCallback(() => {
    // We could narrow by type, but invalidating all caches is cheap and covers
    // every mutation path (fire, bump, item toggle, void, recall, serve). The
    // *_PREFIX keys match every station-filtered variant. Completed is included
    // so a bump moves a ticket into the Completed lane live.
    queryClient.invalidateQueries({ queryKey: ACTIVE_ORDERS_KEY_PREFIX })
    queryClient.invalidateQueries({ queryKey: TAKEAWAY_READY_KEY })
    queryClient.invalidateQueries({ queryKey: KITCHEN_STATS_KEY })
    queryClient.invalidateQueries({ queryKey: COMPLETED_ORDERS_KEY_PREFIX })
    // Catering lane (kitchen flow). No-op when the lane is off: the query
    // never ran, so there is nothing to invalidate.
    queryClient.invalidateQueries({ queryKey: CATERING_KDS_QK })
  }, [queryClient])

  const streamStatus = useKitchenStream({ enabled: true, onEvent: handleEvent })

  // Reconcile the board on SSE RE-connect. The kitchen stream has no replay
  // (no Last-Event-ID) and the server drops events to a full/slow client, so
  // anything fired while the stream was down — a network blip, a tablet asleep,
  // and every Railway redeploy (all sockets drop on restart) — is otherwise
  // only picked up by the 30s poll, leaving the board stale in the meantime.
  // When the stream returns to 'live' after having dropped, force an immediate
  // refetch. The very first connect is skipped (the initial query already
  // loads the board).
  const hasBeenLiveRef = useRef(false)
  useEffect(() => {
    if (streamStatus !== 'live') return
    if (hasBeenLiveRef.current) {
      handleEvent()
    } else {
      hasBeenLiveRef.current = true
    }
  }, [streamStatus, handleEvent])

  // ── Derived ──

  const orders = (ordersResponse || []) as Order[]

  // ── New-order alert ──
  // Play a distinct chime whenever new work lands on the line — a brand-new
  // ticket OR fresh items fired onto an EXISTING ticket. A re-fire keeps the
  // same order id, so tracking ids alone stayed silent when a server added items
  // to an open tab (the kitchen never heard the addition). Each fire wave stamps
  // a higher kot_fire_generation (kot.go), so we track the highest generation
  // seen per order and alert when a new order appears or its generation rises.
  // Voiding/bumping don't change generation, so neither false-chimes. The first
  // population is recorded silently so we only alert on genuine changes.
  const prevGenRef = useRef<Map<string, number> | null>(null)
  useEffect(() => {
    const genOf = (o: Order) =>
      (o.items ?? []).reduce((m, i) => Math.max(m, i.kot_fire_generation ?? 0), 0)
    const prev = prevGenRef.current
    if (prev !== null && soundEnabled) {
      const arrived = orders.some(
        (o) => o.status !== 'ready' && (!prev.has(o.id) || genOf(o) > (prev.get(o.id) ?? 0)),
      )
      // One alert covers a burst — cooks just need to know "something new landed".
      if (arrived) kitchenSoundService.playNewOrderSound('kds')
    }
    prevGenRef.current = new Map(orders.map((o) => [o.id, genOf(o)]))
  }, [orders, soundEnabled])

  const filteredOrders = useMemo(() => {
    if (liveFilter === 'all') return orders
    if (liveFilter === 'urgent') {
      return orders.filter((o) => {
        if (o.status === 'ready') return false
        const start = o.kot_first_sent_at || o.created_at
        if (!start) return false
        // Grade each ticket against its PER-CHANNEL prep target (matches the
        // card color tiers, which turn 'urgent' at ≥90% of the same target).
        const targetMs = kitchen.prepTargetFor(o.order_type) * 60 * 1000 * 0.9
        return Date.now() - new Date(start).getTime() >= targetMs
      })
    }
    return orders.filter((o) => o.order_type === liveFilter)
  }, [orders, liveFilter, kitchen])

  // Sort: critical → urgent → fresh-by-age; ready tickets always at the end.
  const sortedOrders = useMemo(() => {
    const now = Date.now()
    const elapsedOf = (o: Order): number => {
      const start = o.kot_first_sent_at || o.created_at
      if (!start) return 0
      return now - new Date(start).getTime()
    }
    // 'ready' (at-pass) tickets always sink to the end regardless of sort order.
    // Among active tickets: oldest-first = larger elapsed first; newest-first
    // flips that so the freshest KOT lands top-left.
    const dir = sortOrder === 'newest' ? -1 : 1
    return [...filteredOrders].sort((a, b) => {
      const ar = a.status === 'ready'
      const br = b.status === 'ready'
      if (ar !== br) return ar ? 1 : -1
      if (ar && br) return 0
      return dir * (elapsedOf(b) - elapsedOf(a))
    })
  }, [filteredOrders, sortOrder])

  const urgentCount = useMemo(() => {
    return orders.filter((o) => {
      if (o.status === 'ready') return false
      const start = o.kot_first_sent_at || o.created_at
      if (!start) return false
      const targetMs = kitchen.prepTargetFor(o.order_type) * 60 * 1000 * 0.9
      return Date.now() - new Date(start).getTime() >= targetMs
    }).length
  }, [orders, kitchen])

  const typeCounts = useMemo(() => {
    const base = { dine_in: 0, takeout: 0, delivery: 0 } as Record<string, number>
    for (const o of orders) base[o.order_type] = (base[o.order_type] || 0) + 1
    return base
  }, [orders])

  const atPassCount = useMemo(() => orders.filter((o) => o.status === 'ready').length, [orders])

  // ── Lane partitions ──
  // Preparing = still cooking (keeps the urgency sort from sortedOrders).
  // Ready = bumped / at-pass, most-recently-ready first so a fresh pickup lands
  // at the top of its lane. These are the same tickets sortedOrders holds, just
  // split in two — side-by-side lanes at lg+, Current | Ready tabs below lg.
  const preparingOrders = useMemo(
    () => sortedOrders.filter((o) => o.status !== 'ready'),
    [sortedOrders],
  )
  const readyOrders = useMemo(
    () =>
      filteredOrders
        .filter((o) => o.status === 'ready')
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [filteredOrders],
  )
  // Long-waiting takeaway that has aged past the live board's stale window is no
  // longer in `orders` (getKitchenOrders drops it), but still needs pickup — fold
  // those extras into the Ready lane so nothing waiting at the pass is lost.
  const extraTakeawayReady = useMemo(() => {
    const known = new Set(readyOrders.map((o) => o.id))
    return (takeawayReady as Order[]).filter((o) => !known.has(o.id))
  }, [takeawayReady, readyOrders])
  // Takeaway pickups only make sense under the All / Takeaway / Urgent views —
  // hide them when the cook has filtered to dine-in or delivery.
  const showTakeawayExtras =
    (liveFilter === 'all' || liveFilter === 'takeout' || liveFilter === 'urgent') &&
    extraTakeawayReady.length > 0
  const readyLaneCount = readyOrders.length + (showTakeawayExtras ? extraTakeawayReady.length : 0)

  // Fire the Ready tab's "+1" pop whenever the at-pass count grows. First
  // population is recorded silently (mirrors the new-order sound guard).
  const prevReadyCountRef = useRef<number | null>(null)
  useEffect(() => {
    const prev = prevReadyCountRef.current
    prevReadyCountRef.current = readyLaneCount
    if (prev !== null && readyLaneCount > prev) setReadyPopNonce((n) => n + 1)
  }, [readyLaneCount])

  // Ready-tab escalation: a ticket forgotten at the pass while the cook stares
  // at the Current tab is the classic tabbed-KDS failure, so once anything has
  // been ready past the threshold the badge goes red and pulses. The 30s tick
  // re-evaluates age even when no order data changes.
  const READY_ESCALATION_MS = 5 * 60 * 1000
  const [nowTick, setNowTick] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])
  const readyStalled = useMemo(() => {
    const stale = (o: Order) => nowTick - new Date(o.updated_at).getTime() >= READY_ESCALATION_MS
    return (
      readyOrders.some(stale) ||
      (showTakeawayExtras && extraTakeawayReady.some(stale))
    )
  }, [readyOrders, extraTakeawayReady, showTakeawayExtras, nowTick, READY_ESCALATION_MS])

  // ── Mutations ──

  const markPickedUpMutation = useMutation({
    // F-KDS-N5: kitchen-scoped hand-off. This used to call
    // updateOrderStatus(orderId, 'served') → PATCH /orders/:id/status, which is
    // gated counter/manager/admin (F-CTRL-05), so every tap from a kitchen-role
    // station failed with 403 insufficient_permissions and the pass could never
    // clear a ticket. Keep this on the /kitchen route.
    mutationFn: (orderId: string) => apiClient.markOrderServed(orderId),
    onSuccess: () => {
      handleEvent()
      toast({
        title: 'Ticket cleared',
        description: 'Order marked as picked up and removed from the line.',
      })
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not update order',
        description: err.message || 'Try again.',
      })
    },
  })

  const bumpMutation = useMutation({
    mutationFn: async (order: Order) => {
      const id = typeof order?.id === 'string' ? order.id.trim() : ''
      if (!id) throw new Error('Missing order id — refresh the kitchen screen')
      // When a specific station is selected, bump only that station's lines; the
      // order stays live on the other stations' boards until they bump too. On
      // the "All" board we omit the station so the whole order is bumped.
      const res = await apiClient.kitchenBumpOrder(id, stationFilter !== 'all' ? stationFilter : undefined)
      if (!res.success) throw new Error(res.message || 'Could not mark as done')
      return { res, order }
    },
    onError: (err: Error) => {
      const msg = err.message || ''
      if (msg.includes('Order not found') || msg.includes('Network Error') || msg.includes('ECONNREFUSED')) {
        handleEvent()
      }
      toast({
        variant: 'destructive',
        title: 'Could not mark order ready',
        description:
          msg.includes('Network Error') || msg.includes('ECONNREFUSED')
            ? 'API unreachable — check that the backend is running, then try again.'
            : msg,
      })
    },
    onSuccess: ({ res, order }) => {
      const data = res.data
      if (data?.ready_for_pickup) {
        publishOrderReady({
          type: 'order_ready_for_pickup',
          orderId: order.id,
          orderNumber: order.order_number,
          tableId: (data.table_id as string | undefined) ?? order.table_id ?? null,
          completionSeconds: data.completion_seconds ?? 0,
          kitchenBumpedAt:
            typeof data.kitchen_bumped_at === 'string' ? data.kitchen_bumped_at : new Date().toISOString(),
        })
      }
      handleEvent()
      // Bump confirmation — order_ready for dine-in, takeaway_ready for takeout.
      if (soundEnabled) kitchenSoundService.playOrderReadySound(order.id, order.order_type)
    },
  })

  const recallMutation = useMutation({
    mutationFn: async (orderId: string) => apiClient.recallOrder(orderId),
    onSuccess: () => {
      handleEvent()
      toast({ title: 'Ticket recalled', description: 'Order is back on the line.' })
    },
    onError: (err: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not recall order',
        description: err.message || 'The recall window may have expired.',
      })
    },
  })

  // Optimistically flip one line's status in every cached station-filter
  // variant so the green check lands on the tap itself, not after the PATCH
  // round-trips. The select() runs on the patched cache, so isItemPrepared
  // re-derives immediately. handleEvent() (and the SSE item_updated event)
  // reconcile against the server right after.
  const patchCachedItemStatus = useCallback(
    (orderId: string, itemId: string, status: OrderItem['status']) => {
      queryClient.setQueriesData<{ data?: Order[] }>(
        { queryKey: ACTIVE_ORDERS_KEY_PREFIX },
        (old) => {
          if (!old?.data) return old
          return {
            ...old,
            data: old.data.map((o) =>
              o.id === orderId
                ? {
                    ...o,
                    items: (o.items ?? []).map((it) =>
                      it.id === itemId ? { ...it, status } : it,
                    ),
                  }
                : o,
            ),
          }
        },
      )
    },
    [queryClient],
  )

  const handleItemTogglePrepared = async (orderId: string, itemId: string, prepared: boolean) => {
    const next = prepared ? 'ready' : 'sent'
    const snapshot = queryClient.getQueriesData({ queryKey: ACTIVE_ORDERS_KEY_PREFIX })
    patchCachedItemStatus(orderId, itemId, next) // instant green / un-green
    try {
      await apiClient.updateOrderItemStatus(orderId, itemId, next)
      handleEvent()
    } catch (err) {
      // Roll the optimistic change back to exactly what was on screen before.
      snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data))
      toast({
        variant: 'destructive',
        title: 'Could not update item',
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  // Long-press → intermediate "preparing" (started) / back to sent.
  const handleItemSetStatus = async (
    orderId: string,
    itemId: string,
    status: 'sent' | 'preparing' | 'ready',
  ) => {
    const snapshot = queryClient.getQueriesData({ queryKey: ACTIVE_ORDERS_KEY_PREFIX })
    patchCachedItemStatus(orderId, itemId, status) // instant "Cooking" amber
    try {
      await apiClient.updateOrderItemStatus(orderId, itemId, status)
      handleEvent()
    } catch (err) {
      snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data))
      toast({
        variant: 'destructive',
        title: 'Could not update item',
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  // ── Keyboard / bump-bar control ──
  // Bump bars emulate a keyboard, so these shortcuts double as hands-free
  // hardware support: 1–9 jump to a ticket, arrows move focus, Enter bumps
  // (or picks up an at-pass ticket), R/U recalls. Typing in a field is never
  // hijacked. sortedOrders is the on-screen order, so indices line up.
  useEffect(() => {
    const isBumpable = (o: Order) => {
      if (o.status === 'ready') return false
      const items = o.items ?? []
      const active = items.filter((i) => i.status !== 'voided')
      // All lines voided → nothing to cook, but the ticket must still be
      // clearable via Enter/bump-bar (mirrors KOTCard's allActivePrepared).
      if (active.length === 0) return items.length > 0
      return active.every((i) => i.status === 'ready' || i.status === 'served')
    }
    const onKey = (e: KeyboardEvent) => {
      // A recipe card is open over the board: its keys are its own. Enter on
      // one of its buttons must never bump the ticket behind it.
      if (recipeOpen) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      // Catering board: the shortcuts belong to the order tickets, which are
      // not on screen. Enter is swallowed too — a bump bar is a keyboard, and
      // its Enter would otherwise re-press the dish a cook last tapped
      // (the focused button) and untick it.
      if (showingCatering) {
        if (e.key === 'Enter') e.preventDefault()
        return
      }
      const list = sortedOrders
      if (list.length === 0) return
      const idx = focusedOrderId ? list.findIndex((o) => o.id === focusedOrderId) : -1

      if (/^[1-9]$/.test(e.key)) {
        const n = parseInt(e.key, 10) - 1
        if (n < list.length) {
          setFocusedOrderId(list[n].id)
          e.preventDefault()
        }
        return
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setFocusedOrderId(list[idx < 0 ? 0 : Math.min(list.length - 1, idx + 1)].id)
        e.preventDefault()
        return
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setFocusedOrderId(list[idx < 0 ? 0 : Math.max(0, idx - 1)].id)
        e.preventDefault()
        return
      }
      if (e.key === 'Escape') {
        setFocusedOrderId(null)
        return
      }
      const focused = idx >= 0 ? list[idx] : null
      if (!focused) return
      if (e.key === 'Enter') {
        e.preventDefault()
        // Bump bars emulate a keyboard; a double-press or key-repeat must not
        // fire a second bump/pickup while the first is still in flight (the
        // click path is guarded by `disabled`, but this keyboard path bypasses
        // it). Guard on the mutation's in-flight state.
        if (focused.status === 'ready') {
          if (!markPickedUpMutation.isPending) markPickedUpMutation.mutate(focused.id)
        } else if (isBumpable(focused)) {
          if (!bumpMutation.isPending) bumpMutation.mutate(focused)
        } else {
          toast({ title: 'Not ready', description: 'Mark every item prepared before marking done.' })
        }
        return
      }
      if ((e.key === 'r' || e.key === 'u') && focused.status === 'ready' && kitchen.recallWindowSeconds > 0) {
        e.preventDefault()
        if (!recallMutation.isPending) recallMutation.mutate(focused.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    sortedOrders,
    focusedOrderId,
    bumpMutation,
    recallMutation,
    markPickedUpMutation,
    kitchen.recallWindowSeconds,
    toast,
    recipeOpen,
    showingCatering,
  ])

  // Keep the keyboard-focused ticket scrolled into view.
  useEffect(() => {
    if (!focusedOrderId) return
    document
      .getElementById(`kds-card-${focusedOrderId}`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [focusedOrderId])

  const errorMessage =
    error instanceof Error ? error.message : error ? String(error) : 'Unknown error'

  // One ticket card, wired to every handler. Shared by the small-screen single
  // grid and both wide-screen lanes so the card behaviour is identical everywhere.
  const renderKotCard = (order: Order) => (
    <div key={order.id} id={`kds-card-${order.id}`} className="flex">
      <KOTCard
        order={order as Order & { kot_first_sent_at?: string; server_name?: string }}
        targetPrepMinutes={kitchen.prepTargetFor(order.order_type)}
        staleMinutes={kitchen.staleMinutes}
        groupByStation={viewMode === 'expo'}
        focused={order.id === focusedOrderId}
        isAtPass={order.status === 'ready'}
        onItemTogglePrepared={handleItemTogglePrepared}
        onItemSetStatus={handleItemSetStatus}
        onBump={async (id) => {
          const o = orders.find((x) => x.id === id) ?? order
          await bumpMutation.mutateAsync(o)
        }}
        bumpLoading={bumpMutation.isPending && bumpMutation.variables?.id === order.id}
        onPickedUp={(id) => markPickedUpMutation.mutate(id)}
        pickedUpLoading={
          markPickedUpMutation.isPending && markPickedUpMutation.variables === order.id
        }
        onRecall={
          order.status === 'ready' && kitchen.recallWindowSeconds > 0
            ? (id) => recallMutation.mutate(id)
            : undefined
        }
        onOpenRecipe={recipes.enabled ? openRecipe : undefined}
        hasRecipe={recipes.hasDish}
        recallLoading={recallMutation.isPending && recallMutation.variables === order.id}
      />
    </div>
  )

  // Extra long-waiting takeaway (aged past the live board's stale window) — a
  // simple pickup card, shown at the foot of the Ready lane / grid.
  const renderTakeawayExtras = () =>
    extraTakeawayReady.map((o) => (
      <TakeawayReadyCard
        key={o.id}
        order={o}
        onPickedUp={() => markPickedUpMutation.mutate(o.id)}
        loading={markPickedUpMutation.isPending && markPickedUpMutation.variables === o.id}
      />
    ))

  // ── Render ──

  return (
    // Fill the parent container, don't force 100vh. When an admin views the KDS
    // it's nested inside AdminLayout's content box (already dvh − header), so
    // `min-h-screen` made the board taller than its box and the bottom scrolled
    // off / got cut. `h-full min-h-0` fills whatever height the layout gives us
    // (AdminLayout content area, or the kitchen-role full-height wrapper), and
    // the inner board owns its own scroll.
    <div className="flex h-full min-h-0 flex-col bg-muted">
      {recipes.enabled && <RecipeCardSheet target={recipeTarget} onClose={() => setRecipeTarget(null)} />}
      {/* Compact top bar — one slim row of identity + live stats + actions, with
          a single scrollable chip row below. Kept tight (~2 short rows) so the
          KOT board owns the screen on small KDS tablets. The app/OS nav already
          reads "Kitchen Display", so the header only needs a small mark + stats. */}
      <header className="shrink-0 border-b border-border bg-card px-3 py-2 shadow-sm sm:px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 shadow-sm">
              <ChefHat className="h-4 w-4 text-white" />
            </div>
            <h1 className="shrink-0 text-base font-semibold tracking-tight text-foreground">
              Kitchen
            </h1>
            <div className="flex min-w-0 items-center gap-1.5 overflow-hidden text-xs text-muted-foreground">
              <StreamStatusDot status={streamStatus} isFetching={isFetching && !isLoading} />
              <span className="whitespace-nowrap tabular-nums">{orders.length} active</span>
              <span className="hidden truncate md:inline">· {user.first_name}</span>
              {kitchenStats && kitchenStats.bumped_count > 0 && (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span
                    className="hidden whitespace-nowrap tabular-nums sm:inline"
                    title="Average prep time (first KOT fire → done), last 24h"
                  >
                    ⌀ {formatPrepTime(kitchenStats.avg_prep_seconds)}
                  </span>
                  <span className="hidden md:inline">·</span>
                  <span
                    className={cn(
                      'hidden whitespace-nowrap tabular-nums md:inline',
                      kitchenStats.on_time_pct >= 80
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : kitchenStats.on_time_pct >= 50
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-red-600 dark:text-red-400',
                    )}
                    title={`On-time vs each channel's prep target (default ${kitchenStats.target_minutes} min), last 24h`}
                  >
                    {kitchenStats.on_time_pct}% on-time
                  </span>
                  <span className="hidden lg:inline">·</span>
                  <span className="hidden whitespace-nowrap tabular-nums lg:inline" title="Tickets completed in the last 24h">
                    {kitchenStats.bumped_count} done
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10"
              onClick={() => setSortOrder((v) => (v === 'newest' ? 'oldest' : 'newest'))}
              aria-label={sortOrder === 'newest' ? 'Newest first — tap for oldest first' : 'Oldest first — tap for newest first'}
              title={sortOrder === 'newest' ? 'Newest ticket first' : 'Oldest ticket first (FIFO)'}
            >
              {sortOrder === 'newest' ? <ArrowDownWideNarrow className="h-4 w-4" /> : <ArrowUpWideNarrow className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4', (isLoading || isFetching) && 'animate-spin')} />
            </Button>
            <Button
              variant={viewMode === 'expo' ? 'default' : 'outline'}
              size="icon"
              className="h-10 w-10"
              onClick={() => setViewMode((v) => (v === 'expo' ? 'grid' : 'expo'))}
              aria-label={viewMode === 'expo' ? 'Switch to grid view' : 'Switch to expo (by-station) view'}
              title={viewMode === 'expo' ? 'Expo view — grouped by station' : 'Grid view'}
            >
              {viewMode === 'expo' ? <ListChecks className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />}
            </Button>
            <Button
              variant={allDayOpen ? 'default' : 'outline'}
              size="icon"
              className="h-10 w-10"
              onClick={() => setAllDayOpen((v) => !v)}
              aria-label={allDayOpen ? 'Hide all-day counts' : 'Show all-day counts'}
              title="All-Day — batch prep counts across active tickets"
            >
              <ClipboardList className="h-4 w-4" />
            </Button>
            <Button
              variant={completedOpen ? 'default' : 'outline'}
              size="icon"
              className="h-10 w-10"
              onClick={() => setCompletedOpen((v) => !v)}
              aria-label={completedOpen ? 'Hide completed orders' : 'Show completed orders'}
              title="Completed — today's finished tickets"
            >
              <History className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setShowSoundSettings((v) => !v)}>
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Filter row — type chips + station chips. Single horizontally-scrollable
            line (not flex-wrap) so it never eats vertical board space on small KDS tablets. */}
        <div className="no-scrollbar mt-2 flex items-center gap-2 overflow-x-auto pb-1 [&>*]:shrink-0">
          {/* "Show:" prefix mirrors the "Station:" label further right, so the two
              separate All chips (order-type vs station) aren't ambiguously identical. */}
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1">
            <Filter className="h-3.5 w-3.5" />
            Show:
          </span>
          <FilterChip
            active={!showingCatering && liveFilter === 'all'}
            onClick={() => { setCateringView(false); setLiveFilter('all') }}
            label="All"
            count={orders.length}
            tone="slate"
          />
          <FilterChip
            active={!showingCatering && liveFilter === 'urgent'}
            onClick={() => { setCateringView(false); setLiveFilter('urgent') }}
            label="Urgent"
            count={urgentCount}
            tone="red"
            icon={<Flame className="h-3.5 w-3.5" />}
          />
          <span className="mx-1 h-5 w-px bg-border" aria-hidden />
          <FilterChip
            active={!showingCatering && liveFilter === 'dine_in'}
            onClick={() => { setCateringView(false); setLiveFilter('dine_in') }}
            label="Dine-in"
            count={typeCounts.dine_in || 0}
            tone="sky"
            icon={<Home className="h-3.5 w-3.5" />}
          />
          <FilterChip
            active={!showingCatering && liveFilter === 'takeout'}
            onClick={() => { setCateringView(false); setLiveFilter('takeout') }}
            label="Takeaway"
            count={typeCounts.takeout || 0}
            tone="violet"
            icon={<ShoppingBag className="h-3.5 w-3.5" />}
          />
          <FilterChip
            active={!showingCatering && liveFilter === 'delivery'}
            onClick={() => { setCateringView(false); setLiveFilter('delivery') }}
            label="Delivery"
            count={typeCounts.delivery || 0}
            tone="indigo"
            icon={<Truck className="h-3.5 w-3.5" />}
          />
          {catering.enabled && (
            <>
              <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              <FilterChip
                active={showingCatering}
                onClick={() => setCateringView((v) => !v)}
                label={CK.chip}
                count={cateringActiveCount}
                tone="purple"
                icon={<CalendarClock className="h-3.5 w-3.5" />}
              />
            </>
          )}
          {stations.length > 0 && (
            <>
              <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1">
                <Filter className="h-3.5 w-3.5" />
                Station:
              </span>
              <FilterChip
                active={stationFilter === 'all'}
                onClick={() => setStationFilter('all')}
                label="All"
                tone="slate"
              />
              {stations.map((s) => (
                <FilterChip
                  key={s.id}
                  active={stationFilter === s.id}
                  onClick={() => setStationFilter(s.id)}
                  label={s.name}
                  tone={s.output_type === 'kds' ? 'teal' : 'amber'}
                  icon={<Radio className="h-3.5 w-3.5" />}
                />
              ))}
            </>
          )}
          {atPassCount > 0 && (
            <Badge className="ml-auto bg-emerald-600 text-white gap-1.5 px-3 py-1 text-sm">
              <Package className="h-3.5 w-3.5" />
              {atPassCount} at pass
            </Badge>
          )}
        </div>
      </header>

      {showSoundSettings && (
        <SoundPopover
          enabled={soundEnabled}
          volume={volume}
          onEnabled={setSoundEnabled}
          onVolume={setVolume}
          onClose={() => setShowSoundSettings(false)}
        />
      )}

      {allDayOpen && (
        <AllDayPanel orders={preparingOrders} onClose={() => setAllDayOpen(false)} />
      )}

      {/* Board — responsive:
          • lg+  → Preparing | Ready lanes side-by-side, plus an optional
                   Completed history lane on the right.
          • < lg → Current | Ready tabs (full width each — two side-by-side
                   lanes are too cramped on a tablet); the Ready tab announces
                   itself via count badge + red pulse when something has waited
                   too long at the pass. Completed shows as a collapsible
                   section below either tab. */}
      <div className="flex min-h-0 flex-1">
        <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {showingCatering ? (
            <CateringBoard
              tickets={cateringTickets}
              stationFilter={stationFilter}
              isLoading={cateringLoading}
              isError={cateringError}
              onRetry={() => void refetchCatering()}
              daysBefore={catering.setting.show_days_before}
              hasRecipe={recipes.hasDish}
              onOpenRecipe={
                recipes.enabled
                  ? (t, l) =>
                      setRecipeTarget({
                        kind: 'product',
                        productId: l.product_id,
                        name: l.product_name,
                        qty: l.quantity,
                        orderType: t.order_type,
                        options: l.options,
                      })
                  : undefined
              }
              notify={(kind, message) => toast({ title: message, variant: kind === 'error' ? 'destructive' : undefined })}
            />
          ) : isLoading ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3">
              <RefreshCw className="h-10 w-10 animate-spin text-orange-600" />
              <p className="text-base text-muted-foreground">Loading kitchen queue…</p>
            </div>
          ) : (
            <>
              {error && (
                <div
                  role="alert"
                  className="m-3 flex shrink-0 flex-col gap-3 rounded-xl border border-danger-border bg-danger-surface p-4 shadow-sm sm:m-4 sm:flex-row sm:items-center"
                >
                  <AlertCircle className="h-8 w-8 shrink-0 text-red-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-danger-fg">Could not load orders</p>
                    <p className="mt-1 break-words text-sm text-danger-fg">{errorMessage}</p>
                  </div>
                  <Button className="shrink-0" onClick={() => refetch()}>
                    Try again
                  </Button>
                </div>
              )}

              {/* ── Wide (lg+): Preparing | Ready | Completed lanes ── */}
              <div className="hidden min-h-0 flex-1 lg:flex">
                <Lane title="Preparing" count={preparingOrders.length} tone="amber">
                  {preparingOrders.length === 0 ? (
                    <LaneEmpty label="Nothing cooking" />
                  ) : (
                    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] content-start">
                      {preparingOrders.map(renderKotCard)}
                    </div>
                  )}
                </Lane>
                <Lane title="Ready" count={readyLaneCount} tone="emerald">
                  {readyLaneCount === 0 ? (
                    <LaneEmpty label="Nothing at the pass" />
                  ) : (
                    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] content-start">
                      {readyOrders.map(renderKotCard)}
                      {showTakeawayExtras && renderTakeawayExtras()}
                    </div>
                  )}
                </Lane>
                {completedOpen && (
                  <CompletedLane
                    orders={completedOrders}
                    loading={completedLoading}
                    onClose={() => setCompletedOpen(false)}
                  />
                )}
              </div>

              {/* ── Small (< lg): Current | Ready tabs ── */}
              <div className="flex min-h-0 flex-1 flex-col lg:hidden">
                {/* Tab bar — same markup + orange pop badge as the counter's
                    narrow-mode Menu/Ticket toggle, so both tablet screens share
                    one visual language. */}
                <div
                  role="tablist"
                  aria-label="Kitchen queue"
                  className="flex shrink-0 gap-2 border-b border-border bg-card px-3 py-2"
                >
                  <Button
                    type="button"
                    role="tab"
                    aria-selected={smallTab === 'current'}
                    variant={smallTab === 'current' ? 'default' : 'outline'}
                    size="sm"
                    className="min-h-11 flex-1 touch-manipulation"
                    onClick={() => setSmallTab('current')}
                  >
                    Current
                    {preparingOrders.length > 0 && (
                      <Badge
                        key={preparingOrders.length}
                        className="counter-tab-badge-pop ml-2 border-transparent bg-orange-500 px-1.5 py-0 text-xs font-semibold tabular-nums text-white"
                        aria-label={`${preparingOrders.length} ticket${preparingOrders.length === 1 ? '' : 's'} preparing`}
                      >
                        {preparingOrders.length}
                      </Badge>
                    )}
                  </Button>
                  <Button
                    type="button"
                    role="tab"
                    aria-selected={smallTab === 'ready'}
                    variant={smallTab === 'ready' ? 'default' : 'outline'}
                    size="sm"
                    className="relative min-h-11 flex-1 touch-manipulation"
                    onClick={() => setSmallTab('ready')}
                  >
                    Ready
                    {readyLaneCount > 0 && (
                      <Badge
                        key={readyLaneCount}
                        className={cn(
                          'counter-tab-badge-pop ml-2 border-transparent px-1.5 py-0 text-xs font-semibold tabular-nums text-white',
                          readyStalled
                            ? 'animate-pulse bg-red-600 hover:bg-red-600'
                            : 'bg-orange-500 hover:bg-orange-500',
                        )}
                        aria-label={`${readyLaneCount} order${readyLaneCount === 1 ? '' : 's'} at the pass${readyStalled ? ' — waiting too long' : ''}`}
                      >
                        {readyLaneCount}
                      </Badge>
                    )}
                    {readyPopNonce > 0 && (
                      <span
                        key={readyPopNonce}
                        aria-hidden
                        className="counter-product-plus-one pointer-events-none absolute -top-2 right-2 z-20 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white shadow-md"
                      >
                        +1
                      </span>
                    )}
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-3 sm:p-4">
                  {smallTab === 'current' ? (
                    preparingOrders.length === 0 ? (
                      <EmptyState />
                    ) : (
                      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))] content-start">
                        {preparingOrders.map(renderKotCard)}
                      </div>
                    )
                  ) : readyLaneCount === 0 ? (
                    <LaneEmpty label="Nothing at the pass" />
                  ) : (
                    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))] content-start">
                      {readyOrders.map(renderKotCard)}
                      {showTakeawayExtras && renderTakeawayExtras()}
                    </div>
                  )}
                  {completedOpen && (
                    <div className="mt-6 border-t border-dashed border-border pt-4">
                      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-foreground">
                        <History className="h-5 w-5" />
                        Completed today
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold tabular-nums text-secondary-foreground">
                          {completedOrders.length}
                        </span>
                      </h2>
                      <CompletedList orders={completedOrders} loading={completedLoading} />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}

// ── Sub-components ──

interface FilterChipProps {
  active: boolean
  onClick: () => void
  label: string
  count?: number
  tone: 'slate' | 'sky' | 'violet' | 'indigo' | 'red' | 'amber' | 'teal' | 'purple'
  icon?: React.ReactNode
}

/*
 * Station filter chips.
 *
 * `idle` used to be a literal `bg-card` on all seven, which is what put seven
 * white blocks on a wall-mounted KDS the moment the theme went dark
 * (F-FE-N55). The surface is now `bg-card` — still exactly white in light
 * mode, and on the elevation ramp in dark — while each station keeps its own
 * hue in the text and border, because a station colour is an IDENTITY the
 * kitchen learns, not a status. Only the ink shade flips per theme: the 800
 * weights are unreadable on a dark surface, the 300s unreadable on a white one.
 *
 * The amber `active` chip carries dark ink rather than white: white on
 * amber-500 measures 2.1:1, which fails WCAG at any size. Every other active
 * chip clears 4.5:1 with white.
 */
const TONE_CLASSES: Record<FilterChipProps['tone'], { active: string; idle: string }> = {
  slate: {
    active: 'bg-primary text-primary-foreground border-primary',
    idle: 'bg-card text-foreground border-border hover:border-muted-foreground',
  },
  sky: {
    active: 'bg-sky-600 text-white border-sky-600',
    idle: 'bg-card text-sky-800 dark:text-sky-300 border-sky-200 dark:border-sky-800 hover:border-sky-400',
  },
  violet: {
    active: 'bg-violet-600 text-white border-violet-600',
    idle: 'bg-card text-violet-800 dark:text-violet-300 border-violet-200 dark:border-violet-800 hover:border-violet-400',
  },
  indigo: {
    active: 'bg-indigo-600 text-white border-indigo-600',
    idle: 'bg-card text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:border-indigo-400',
  },
  red: {
    active: 'bg-red-600 text-white border-red-600',
    idle: 'bg-card text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 hover:border-red-400',
  },
  amber: {
    active: 'bg-amber-500 text-amber-950 border-amber-500',
    idle: 'bg-card text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:border-amber-400',
  },
  purple: {
    active: 'bg-purple-600 text-white border-purple-600',
    idle: 'bg-card text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:border-purple-400',
  },
  teal: {
    active: 'bg-teal-600 text-white border-teal-600',
    idle: 'bg-card text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800 hover:border-teal-400',
  },
}

function FilterChip({ active, onClick, label, count, tone, icon }: FilterChipProps) {
  const styles = TONE_CLASSES[tone]
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ',
        active ? styles.active : styles.idle,
      )}
    >
      {icon}
      {label}
      {typeof count === 'number' && count > 0 && (
        <span
          className={cn(
            'ml-0.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums',
            active ? 'bg-white/25' : 'bg-secondary text-secondary-foreground',
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

function StreamStatusDot({
  status,
  isFetching,
}: {
  status: 'connecting' | 'live' | 'offline'
  isFetching: boolean
}) {
  const meta =
    status === 'live'
      ? { label: isFetching ? 'Live · syncing' : 'Live', dot: 'bg-emerald-500 animate-pulse' }
      : status === 'connecting'
        ? { label: 'Connecting', dot: 'bg-amber-500 animate-pulse' }
        : { label: 'Offline · polling', dot: 'bg-muted-foreground' }
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  )
}

function EmptyState() {
  return (
    <div className="flex h-[50vh] flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
        <ChefHat className="h-10 w-10 text-muted-foreground" />
      </div>
      <p className="text-lg font-semibold text-foreground">
        No tickets on the line
      </p>
      <p className="mt-1.5 text-sm text-muted-foreground max-w-md">
        Fired KOTs will appear here as cards sorted by urgency. You can filter by order type or station above.
      </p>
    </div>
  )
}

/** A vertical lane (wide layout) with a sticky, colour-coded header + count and
 *  its own scroll, so each state's tickets stay visually grouped. */
function Lane({
  title,
  count,
  tone,
  children,
}: {
  title: string
  count: number
  tone: 'amber' | 'emerald'
  children: React.ReactNode
}) {
  const toneCls =
    tone === 'emerald'
      ? 'text-emerald-700 dark:text-emerald-400'
      : 'text-amber-700 dark:text-amber-400'
  return (
    <section className="flex min-w-0 flex-1 flex-col border-r border-border last:border-r-0">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-muted/95 px-3 py-2 backdrop-blur">
        <h2 className={cn('text-sm font-bold uppercase tracking-wide', toneCls)}>{title}</h2>
        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-secondary px-1.5 text-xs font-bold tabular-nums text-secondary-foreground">
          {count}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-3">{children}</div>
    </section>
  )
}

/** Full-lane placeholder shown when a lane has no tickets. */
function LaneEmpty({ label }: { label: string }) {
  return (
    <div className="flex h-40 items-center justify-center text-center text-sm text-muted-foreground">
      {label}
    </div>
  )
}

/** Wide-layout Completed lane — a fixed-width history column with a close button. */
function CompletedLane({
  orders,
  loading,
  onClose,
}: {
  orders: Order[]
  loading: boolean
  onClose: () => void
}) {
  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-border bg-card">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-muted/95 px-3 py-2 backdrop-blur">
        <h2 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-muted-foreground">
          <History className="h-4 w-4" />
          Completed
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-secondary px-1.5 text-xs font-bold tabular-nums text-secondary-foreground">
            {orders.length}
          </span>
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide completed lane"
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent"
        >
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        <CompletedList orders={orders} loading={loading} />
      </div>
    </aside>
  )
}

/** Grid of compact, view-only completed cards. Shared by the wide lane and the
 *  small-screen collapsible section. */
function CompletedList({ orders, loading }: { orders: Order[]; loading: boolean }) {
  if (loading && orders.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Loading completed…</p>
  }
  if (orders.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No completed tickets yet today.</p>
  }
  return (
    <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] content-start">
      {orders.map((o) => (
        <CompletedCard key={o.id} order={o} />
      ))}
    </div>
  )
}

/** One finished ticket — reference only (recall lives in the Ready lane). */
function CompletedCard({ order }: { order: Order }) {
  const o = order as Order & {
    kitchen_bumped_at?: string
    completed_at?: string
    table?: { table_number?: string }
  }
  const when = o.kitchen_bumped_at || o.completed_at || o.updated_at
  const label = o.status === 'completed' ? 'Paid' : 'Picked up'
  const who = o.table?.table_number ? `Table ${o.table.table_number}` : o.customer_name || 'Guest'
  return (
    <div className="rounded-lg border border-border bg-muted/60 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-foreground">
          {displayTicketNo(o.order_number)}
        </span>
        <span className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">
          {o.order_type?.replace('_', '-')}
        </span>
      </div>
      <div className="mt-0.5 truncate text-xs text-muted-foreground">{who}</div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <CheckCircle className="h-3 w-3 text-emerald-500" />
          {label}
        </span>
        {when && <span className="tabular-nums">{formatClock(when)}</span>}
      </div>
    </div>
  )
}

/** Local-time HH:MM for the completed-card timestamp. */
function formatClock(ts: string): string {
  const d = new Date(ts)
  if (isNaN(d.getTime())) return ''
  return formatTimeHuman(d) // F-FE-N122: 12-hour, whatever the tablet's locale
}

function TakeawayReadyCard({
  order,
  onPickedUp,
  loading,
}: {
  order: Order
  onPickedUp: () => void
  loading: boolean
}) {
  const waitTime = Math.floor((Date.now() - new Date(order.updated_at).getTime()) / 1000 / 60)
  return (
    <Card className="border-emerald-400 bg-emerald-50/70 dark:bg-emerald-900/20 dark:border-emerald-700">
      <CardContent className="p-3 text-center">
        <div className="text-2xl font-black text-emerald-900 dark:text-emerald-200">
          {displayTicketNo(order.order_number)}
        </div>
        <div className="text-sm font-medium text-foreground truncate">
          {order.customer_name || 'Guest'}
        </div>
        <div className="text-xs text-muted-foreground mb-2">
          {waitTime <= 0 ? 'Just ready' : `Ready ${waitTime}m ago`}
        </div>
        <Button
          size="sm"
          className="w-full h-9 text-sm"
          disabled={loading}
          onClick={onPickedUp}
        >
          {loading ? 'Updating…' : 'Picked up'}
        </Button>
      </CardContent>
    </Card>
  )
}

function SoundPopover({
  enabled,
  volume,
  onEnabled,
  onVolume,
  onClose,
}: {
  enabled: boolean
  volume: number
  onEnabled: (v: boolean) => void
  onVolume: (v: number) => void
  onClose: () => void
}) {
  return (
    <div className="relative z-40">
      <div
        className="absolute right-4 top-2 w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-border bg-card p-4 shadow-xl"
        role="dialog"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold">Sound</span>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
            ×
          </Button>
        </div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm">Enable chimes</label>
          <button
            type="button"
            onClick={() => onEnabled(!enabled)}
            className={cn(
              'w-10 h-5 rounded-full transition-colors relative',
              enabled ? 'bg-primary' : 'bg-input',
            )}
          >
            <span
              className={cn(
                'block h-4 w-4 rounded-full bg-card absolute top-0.5 transition-transform',
                enabled ? 'translate-x-5' : 'translate-x-0.5',
              )}
            />
          </button>
        </div>
        <label className="text-sm block mb-1.5">Volume</label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.1}
          value={volume}
          onChange={(e) => onVolume(parseFloat(e.target.value))}
          className="w-full"
        />
      </div>
    </div>
  )
}

